import {
    ComponentProps,
    forwardRef,
    ReactNode,
    useState,
    useRef,
    useCallback,
    useEffect,
    RefObject,
} from 'react';
import {
    useFloating,
    offset,
    flip,
    shift,
    autoUpdate,
    Placement,
} from '@floating-ui/react';
import { cn } from '~/helpers/tw';
import TooltipContentContainer from './content.container';

const checkIfElementIsOverflowing = (ref: RefObject<HTMLElement> | null) => {
    const el = ref?.current;
    if (!el) return null;

    const isOverflowing =
        el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

    return isOverflowing;
};

interface TooltipProps extends Omit<ComponentProps<'span'>, 'content'> {
    content: ReactNode;
    position?: Placement;
    ignoreOverflow?: boolean;
    container?: Element | null;
}

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
    (
        {
            children,
            content,
            position = 'top',
            ignoreOverflow = false,
            container,
            ...props
        },
        ref
    ) => {
        const [visible, setVisible] = useState(false);
        const textRef = useRef<HTMLSpanElement>(null);

        const { refs, floatingStyles, placement } = useFloating({
            placement: position,
            middleware: [
                offset(8),
                flip({
                    fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                }),
                shift({ padding: 8 }),
            ],
            whileElementsMounted: autoUpdate,
        });

        const isTouchDevice =
            typeof window !== 'undefined' && 'ontouchstart' in window;

        const handleMouseEnter = useCallback(() => {
            if (isTouchDevice) return;
            const el = textRef.current;
            if (!el) return;

            const isOverflowing =
                el.scrollWidth > el.clientWidth ||
                el.scrollHeight > el.clientHeight;

            if (!ignoreOverflow && !isOverflowing) return;

            setVisible(true);
        }, [ignoreOverflow, isTouchDevice]);

        const handleMouseLeave = useCallback(() => {
            if (isTouchDevice) return;
            setVisible(false);
        }, [isTouchDevice]);

        const handleTouch = () => {
            if (!visible) {
                const isOverflowing = checkIfElementIsOverflowing(textRef);

                if (!ignoreOverflow && !isOverflowing) return;
            }
            setVisible((prev) => !prev);
        };

        const setRefs = useCallback(
            (node: HTMLSpanElement | null) => {
                // Update both refs safely
                refs.setReference(node);
                // Assign only if ref object is mutable
                (
                    textRef as React.MutableRefObject<HTMLSpanElement | null>
                ).current = node;
            },
            [refs]
        );

        useEffect(() => {
            if (!isTouchDevice || !visible) return;

            const isNode = (el: unknown): el is Node => el instanceof Node;

            const handleOutsideTouch = (e: TouchEvent) => {
                const target = e.target as Node;
                const refEl = refs.reference.current;
                const floatEl = refs.floating.current;

                const refContains = isNode(refEl)
                    ? refEl.contains(target)
                    : false;
                const floatContains = isNode(floatEl)
                    ? floatEl.contains(target)
                    : false;

                if (!refContains && !floatContains) {
                    setVisible(false);
                }
            };

            document.addEventListener('touchstart', handleOutsideTouch);
            // Cleanup listener on unmount or when visibility changes
            // eslint-disable-next-line consistent-return
            return () =>
                document.removeEventListener('touchstart', handleOutsideTouch);
        }, [isTouchDevice, visible, refs.reference, refs.floating]);

        return (
            <div
                ref={ref}
                className="flex min-w-0"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouch}
            >
                <span
                    ref={setRefs}
                    {...props}
                    className={cn('block truncate', props.className)}
                >
                    {children}
                </span>

                <TooltipContentContainer
                    visible={visible}
                    content={content}
                    placement={placement}
                    floatingStyles={floatingStyles}
                    refs={refs}
                    container={container}
                    type={typeof content === 'string' ? 'text' : 'list'}
                />
            </div>
        );
    }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
